import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Eye, Check, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';

import WelcomeStep from '../client/steps/WelcomeStep';
import ContactDetailsStep from '../client/steps/ContactDetailsStep';
import PersonalInfoStep from '../client/steps/PersonalInfoStep';
import EmploymentStep from '../client/steps/EmploymentStep';
import InvestmentAccountsStep from '../client/steps/InvestmentAccountsStep';
import OtherAssetsStep from '../client/steps/OtherAssetsStep';
import InsurancePolicyStep from '../client/steps/InsurancePolicyStep';
import LiabilitiesStep from '../client/steps/LiabilitiesStep';
import GoalsStep from '../client/steps/GoalsStep';
import RetirementExpensesStep from '../client/steps/RetirementExpensesStep';
import AnnualTaxesStep from '../client/steps/AnnualTaxesStep';
import AttachmentsStep from '../client/steps/AttachmentsStep';
import ReviewStep from '../client/steps/ReviewStep';

const STEP_META = [
  { title: 'Welcome',                fields: ['Introduction'] },
  { title: 'Contact Details',        fields: ['First / Middle / Last Name', 'Gender', 'SSN', 'U.S. Citizen', 'Marital Status', 'Birthdate', 'Email', 'Mobile / Home / Work Phone / Fax', 'Trust or Corporate Account', 'Tax ID', 'POA', 'Check Writing Ability', 'Address / City / State / Zip / Country', 'CPA', 'Attorney'] },
  { title: 'Personal Information',   fields: ['Dependents (First Name, Last Name, Relationship [Child/Grandchild/Other], Birthdate)', 'Estate Documents: hasWill (Y/N), bypassTrust (Y/N), dateLastReviewed, estateNotes'] },
  { title: 'Employment & Income',    fields: ['Employers (Name, Employment Income, Total Income, Liquid Net Worth)', 'Other Sources of Income (Type dropdown: Pension / Royalties / Annuity / Alimony / Rental / Part-Time / Other / Irrevocable Trust / Reverse Mortgage)', 'Co-Client toggle + full contact block'] },
  { title: 'Investment Accounts',    fields: ['Accounts (Type dropdown: Employer Retirement / IRA / Annuity & Tax-Deferred / Taxable-or-Tax-Free / College Savings / HSA — plus Description + Balance)'] },
  { title: 'Other Assets',           fields: ['Assets (Type dropdown: Home & Personal (with sub: Home/Collectible/Personal Property/Vehicle/Other) / Business & Property / Pension & Deferred Comp / Cash Value Life / 529 / Future Cash / Future Property)'] },
  { title: 'Insurance Policy',       fields: ['Policies (Type dropdown: CVL Client-owned / CVL Trust-owned / Non-Cash-Value Life / Other — plus Description + Coverage)'] },
  { title: 'Liabilities',            fields: ['Liabilities (Type dropdown: Total Amount / Credit Cards / Securities Backed Loan / Line of Credit / Personal Note / Taxes Owed / Margin / Education / Other)'] },
  { title: 'Financial Goals',        fields: ['Retirement Goals (Importance, Age to Retire, Life Expectancy)', 'College Goals (Child, Year, Years, Cost, Importance)', 'Other Goals (Goal, Year, Amount)', 'Public School (For, Start, End, Annual)', 'Private School (For, Start, End, Annual)'] },
  { title: 'Retirement & Basic Living Expenses', fields: ['Basic Living Expenses (Description, Amount, Frequency [Monthly/Annually])', 'Expenses Ending at Retirement (Description, Year End, Amount, Inflate Y/N)', 'Retirement Income Needed (Value, Per Month/Year, Inflate Y/N)', 'Base Inflation Rate (Y/N + %)', 'Or base inflation rate + additional', 'Relocation (State, When, Move Year)'] },
  { title: 'Annual Taxes',           fields: ['Federal Income Taxes', 'State Income Taxes', 'FICA', 'Real Estate Taxes', 'Other Taxes'] },
  { title: 'Attachments',            fields: ['File uploads (PDF, DOC, XLS, CSV, image) — up to 10 files, 25MB each'] },
  { title: 'Review & Submit',        fields: ['Summary of all sections + submit button'] },
];

export default function FormPreview() {
  const [step, setStep] = useState(1);
  const [previewData, setPreviewData] = useState({});
  const [previewFiles] = useState([]);

  // Sandbox updater — mutates local preview state only, never persists
  const updatePreview = (section) => (data) => {
    setPreviewData((prev) => ({ ...prev, [section]: { ...(prev[section] || {}), ...data } }));
  };
  const noopFileUpload = (e) => { e.target.value = ''; };
  const noopFileDelete = () => {};
  const noopSubmit = () => {};

  const renderStep = () => {
    switch (step) {
      case 1: return <WelcomeStep />;
      case 2: return <ContactDetailsStep formData={previewData.contact || {}} updateFormData={updatePreview('contact')} />;
      case 3: return <PersonalInfoStep formData={previewData.personal || {}} updateFormData={updatePreview('personal')} />;
      case 4: return <EmploymentStep formData={previewData.employment || {}} updateFormData={updatePreview('employment')} />;
      case 5: return <InvestmentAccountsStep formData={previewData.investments || {}} updateFormData={updatePreview('investments')} />;
      case 6: return <OtherAssetsStep formData={previewData.otherAssets || {}} updateFormData={updatePreview('otherAssets')} />;
      case 7: return <InsurancePolicyStep formData={previewData.insurance || {}} updateFormData={updatePreview('insurance')} />;
      case 8: return <LiabilitiesStep formData={previewData.liabilitiesSection || {}} updateFormData={updatePreview('liabilitiesSection')} />;
      case 9: return <GoalsStep formData={previewData.goals || {}} updateFormData={updatePreview('goals')} />;
      case 10: return <RetirementExpensesStep formData={previewData.retirement || {}} updateFormData={updatePreview('retirement')} />;
      case 11: return <AnnualTaxesStep formData={previewData.taxes || {}} updateFormData={updatePreview('taxes')} />;
      case 12: return <AttachmentsStep files={previewFiles} onFileUpload={noopFileUpload} onFileDelete={noopFileDelete} />;
      case 13: return <ReviewStep formData={previewData} files={previewFiles} onSubmit={noopSubmit} submitting={false} />;
      default: return null;
    }
  };

  return (
    <div data-testid="form-preview-section">
      {/* Preview banner */}
      <div className="mb-6 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
        <Eye className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
        <div className="text-sm text-amber-900">
          <strong>Preview mode.</strong> This is a live rendering of the current Client Profile Intake Form.
          You can interact with the fields to see how clients will experience them — <em>nothing is saved</em>.
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Step navigator */}
        <aside className="lg:col-span-1">
          <div className="rounded-lg border border-[#e2e8f0] bg-white p-4 sticky top-6">
            <h4 className="mb-3 text-sm font-semibold text-[#0f172a]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Steps
            </h4>
            <ol className="space-y-1">
              {STEP_META.map((meta, idx) => {
                const n = idx + 1;
                const active = n === step;
                // Same required-field rules as the live client form
                let required = false;
                let satisfied = true;
                if (n === 2) {
                  required = true;
                  const c = previewData.contact || {};
                  satisfied = !!(c.firstName || '').trim() && !!(c.lastName || '').trim();
                } else if (n === 4) {
                  required = true;
                  const emps = (previewData.employment && previewData.employment.employers) || [];
                  satisfied = emps.some((e) => e && (
                    (e.employer || '').toString().trim() ||
                    (e.employmentIncome || '').toString().trim() ||
                    (e.totalIncome || '').toString().trim() ||
                    (e.liquidNetWorth || '').toString().trim()
                  ));
                }
                const showCheck = required && satisfied;
                const showWarn = required && !satisfied;
                return (
                  <li key={n}>
                    <button
                      onClick={() => setStep(n)}
                      className={`w-full rounded-md px-3 py-2 text-left text-sm transition-colors ${
                        active
                          ? 'bg-gradient-to-r from-[#0f2444] via-[#1e3a63] to-[#26518c] text-white font-medium'
                          : 'text-[#475569] hover:bg-[#f8fafc]'
                      }`}
                      data-testid={`preview-step-nav-${n}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-xs ${
                          active ? 'bg-white/20 text-white'
                            : showCheck ? 'bg-green-100 text-green-700'
                            : showWarn ? 'bg-red-100 text-red-700'
                            : 'bg-[#e2e8f0] text-[#475569]'
                        }`}>
                          {showCheck ? <Check className="w-3.5 h-3.5" /> : showWarn ? <AlertCircle className="w-3 h-3" /> : n}
                        </span>
                        <span className="flex-1">{meta.title}</span>
                        {required && (
                          <span
                            className={`text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                              active ? 'bg-white/20 text-white'
                                : showCheck ? 'bg-green-100 text-green-700'
                                : 'bg-red-100 text-red-700'
                            }`}
                            data-testid={`preview-step-${showCheck ? 'done' : 'required'}-badge-${n}`}
                          >
                            {showCheck ? 'Done' : 'Required'}
                          </span>
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ol>

            <div className="mt-4 border-t border-[#e2e8f0] pt-3">
              <h5 className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#475569]">
                Fields on this step
              </h5>
              <ul className="space-y-1 text-xs text-[#475569]">
                {STEP_META[step - 1].fields.map((f, i) => (
                  <li key={i} className="leading-snug">• {f}</li>
                ))}
              </ul>
            </div>
          </div>
        </aside>

        {/* Rendered step */}
        <div className="lg:col-span-3">
          <div className="rounded-lg border border-[#e2e8f0] bg-white p-8" data-testid="preview-step-content">
            {renderStep()}
          </div>

          <div className="mt-6 flex justify-between">
            <Button
              onClick={() => setStep((s) => Math.max(1, s - 1))}
              disabled={step === 1}
              variant="outline"
              data-testid="preview-back-button"
            >
              <ChevronLeft className="mr-2 h-4 w-4" /> Back
            </Button>
            <span className="self-center text-sm text-[#475569]" data-testid="preview-step-indicator">
              Step {step} of {STEP_META.length}
            </span>
            <Button
              onClick={() => setStep((s) => Math.min(STEP_META.length, s + 1))}
              disabled={step === STEP_META.length}
              className="bg-[#5b93d6] hover:bg-[#4a7dc5] text-white"
              data-testid="preview-next-button"
            >
              Next <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
